import { ApiError } from '@libraries/typescript/api_client/error';
import { type paths } from '@libraries/typescript/api_client/generated_api_types';
import {
    createRateLimitRecovery,
    createRateLimitSuppressedApiError,
    createRetryWaitBudget,
    finalizeTransparentRateLimitError,
    getRetryAfterDelayMs,
    isTransparentRateLimitRecoveryEligible,
    type RateLimitRetryOwner,
} from '@libraries/typescript/api_client/rate_limit_recovery';
import {
    type ApiBodyType,
    type ApiPathParamsType,
    type ApiQueryParamsType,
    type ApiResponseType,
    type Fetch,
    type FetchResponse,
    type HttpMethod,
    type Mode,
} from '@libraries/typescript/api_client/types';
import { generateUuid } from '@libraries/typescript/utilities';

// Generous per-request timeout. It exists to kill a request that is truly
// stuck (e.g. a hung transpacific connection from APAC to our single
// us-east-1 region), not merely-slow ones, so it sits well above normal
// server processing time.
const REQUEST_TIMEOUT_MS = 30_000;

// Total attempts for a retryable (idempotent) request before giving up: one
// initial try plus two retries. Non-idempotent requests are never retried.
const MAX_REQUEST_ATTEMPTS = 3;

// Base delay for exponential backoff between retries (~0.5s, then ~1s, …).
const RETRY_BASE_DELAY_MS = 500;

export type ApiClientNetworkErrorContext = {
    readonly error: unknown;
    readonly apiUrl: string;
    readonly method: string;
    readonly pathTemplate: string;
    readonly requestId: string;
    readonly attempt: number;
    readonly maxAttempts: number;
    readonly mode?: Mode;
    readonly reason?: string;
    readonly bestEffort?: boolean;
};

export type ApiClientNetworkErrorReporter = (
    context: ApiClientNetworkErrorContext,
) => void;

export type ApiClientConfig = {
    readonly baseUrl: string;
    readonly fetch: Fetch;
    readonly getMode?: (requestedMode?: Mode) => Mode | undefined;
    /** Fallback mode for generated clients used outside the web singleton. */
    readonly defaultMode?: Mode;
    readonly onNetworkError?: ApiClientNetworkErrorReporter;
    readonly onTransparentRateLimitExhausted?: (context: {
        readonly error: ApiError;
        readonly method: HttpMethod;
        readonly signal?: AbortSignal;
        readonly url: string;
    }) => void | Promise<void>;
    readonly clientVersion?: string;
};

export type ApiClient = ReturnType<typeof createApiClient>;

type ApiRequestOptions<
    Path extends keyof paths,
    Method extends keyof paths[Path],
> = {
    readonly url: Path;
    readonly body?: ApiBodyType<Path, Method>;
    readonly pathParams?: ApiPathParamsType<Path>;
    readonly queryParams?: ApiQueryParamsType<Path, Method>;
    readonly mode?: Mode;
    readonly reason?: string;
    readonly requestId?: string;
    readonly signal?: AbortSignal;
    readonly timeoutMs?: number;
    // Suppresses ONLY the 5xx/network retry, for reads where repeating an
    // expensive failed request amplifies backend load. It deliberately does
    // NOT suppress transparent rate-limit recovery: a timeout means the
    // server did the work and we walked away, while a 429 means it declined
    // the work and named a time, so honoring that deadline amplifies nothing.
    readonly retry?: boolean;
    // Mark a non-GET request as server-side idempotent to opt into the same
    // timeout and backoff-retry resilience as GET/HEAD requests.
    readonly idempotent?: boolean;
    /** Controls terminal presentation, not retry eligibility. */
    readonly rateLimitFailureMode?: 'silent' | 'notify';
    // Mark a request whose failure is expected to be harmless so reporters can
    // suppress error-level reporting without changing request behavior.
    readonly bestEffort?: boolean;
};

type RawApiRequestOptions<
    Path extends keyof paths,
    Method extends keyof paths[Path],
> = Omit<ApiRequestOptions<Path, Method>, 'body'> & {
    readonly body?: ApiBodyType<Path, Method> | FormData;
    readonly cache?: RequestCache;
    readonly headers?: Record<string, string>;
    readonly redirect?: RequestRedirect;
};

type DefaultRawApiMethod<Path extends keyof paths> =
    'get' extends keyof paths[Path] ? 'get' : keyof paths[Path];

type ApiValidationIssue = {
    readonly loc?: unknown;
    readonly msg?: unknown;
    readonly message?: unknown;
};

function isRetryableNetworkError(error: unknown): boolean {
    return error instanceof Error && error.name !== 'AbortError';
}

/**
 * True for a request cancelled through an `AbortSignal`.
 *
 * At the `onNetworkError` / `ApiError` boundary this always means a *caller*
 * cancellation — an unmount, a navigation, a superseded request. A stuck
 * connection never surfaces here: `fetchWithTimeout` re-labels its own timeout
 * abort as a timeout error before the error escapes. Reporters can therefore
 * treat this as routine rather than as a network failure.
 */
export function isAbortError(error: unknown): boolean {
    return (
        (error instanceof ApiError && error.isAbortError) ||
        ((error instanceof Error || error instanceof DOMException) &&
            error.name === 'AbortError')
    );
}

export function isRequestTimeoutError(error: unknown): boolean {
    return error instanceof RequestTimeoutError;
}

export function shouldRetryApiClientNetworkError(
    context: Pick<
        ApiClientNetworkErrorContext,
        'error' | 'attempt' | 'maxAttempts'
    >,
): boolean {
    return (
        context.attempt < context.maxAttempts &&
        isRetryableNetworkError(context.error)
    );
}

async function sleep(delayMs: number, signal?: AbortSignal): Promise<void> {
    // Make the backoff interruptible so a caller cancellation during the delay
    // is observed promptly instead of waiting out the full backoff before the
    // next attempt is skipped.
    if (signal?.aborted) {
        return;
    }
    await new Promise<void>((resolve) => {
        let settled = false;
        const finish = (): void => {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };
        const timeoutId = setTimeout(finish, delayMs);
        if (!signal) {
            return;
        }
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timeoutId);
                finish();
            },
            { once: true },
        );
    });
}

function isRetryableMethod(method: string): boolean {
    // Only retry idempotent methods so a retry can never double-submit a
    // mutation (POST/PUT/PATCH/DELETE).
    return method === 'GET' || method === 'HEAD';
}

function isHttpMethod(method: string): method is HttpMethod {
    return (
        method === 'DELETE' ||
        method === 'GET' ||
        method === 'HEAD' ||
        method === 'PATCH' ||
        method === 'POST' ||
        method === 'PUT'
    );
}

function isRetryableHttpStatus(status: number): boolean {
    // The backend owns rate-limit recovery. Retrying 429 in the browser before
    // Retry-After elapses can extend a provider penalty window.
    return status >= 500;
}

function isJsonResponse(response: FetchResponse): boolean {
    const contentType = response.headers.get('content-type');
    if (!contentType) {
        return false;
    }
    const mediaType = contentType.split(';', 1)[0]?.trim().toLowerCase();
    return (
        mediaType === 'application/json' ||
        mediaType?.endsWith('+json') === true
    );
}

function formatValidationIssueLocation(loc: unknown): string | null {
    if (!Array.isArray(loc)) {
        return null;
    }
    const parts = loc
        .filter((part) => part !== 'body')
        .map((part) => String(part));
    return parts.length > 0 ? parts.join('.') : null;
}

function getApiErrorMessage(errorBody: unknown): string | null {
    if (typeof errorBody === 'string' && errorBody) {
        return errorBody;
    }
    if (!errorBody || typeof errorBody !== 'object') {
        return null;
    }

    const body = errorBody as Record<string, unknown>;
    const detail = body['detail'];
    if (typeof detail === 'string') {
        return detail;
    }
    if (
        detail !== null &&
        typeof detail === 'object' &&
        !Array.isArray(detail)
    ) {
        const detailMessage = (detail as Record<string, unknown>)['message'];
        if (typeof detailMessage === 'string') {
            return detailMessage;
        }
    }
    if (Array.isArray(detail)) {
        const firstIssue = (detail as ReadonlyArray<ApiValidationIssue>)[0];
        if (firstIssue === undefined) {
            return null;
        }
        const message = firstIssue?.msg ?? firstIssue?.message;
        if (typeof message === 'string') {
            const location = formatValidationIssueLocation(firstIssue.loc);
            return location ? `${location}: ${message}` : message;
        }
    }
    const message = body['message'];
    if (typeof message === 'string') {
        return message;
    }
    return null;
}

function getApiErrorRequestId(errorBody: unknown): string | undefined {
    if (!errorBody || typeof errorBody !== 'object') {
        return undefined;
    }

    const body = errorBody as Record<string, unknown>;
    const detail = body['detail'];
    if (
        detail !== null &&
        typeof detail === 'object' &&
        !Array.isArray(detail)
    ) {
        const requestId = (detail as Record<string, unknown>)['request_id'];
        if (typeof requestId === 'string') {
            return requestId;
        }
    }
    const requestId = body['request_id'];
    return typeof requestId === 'string' ? requestId : undefined;
}

function getRetryDelayMs(attempt: number): number {
    // Exponential backoff (~0.5s, ~1s, …) with up to 50% jitter so a fleet of
    // clients failing together (e.g. a regional latency spike) doesn't retry
    // in lockstep and stampede the API.
    const backoffMs = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
    return backoffMs + Math.random() * backoffMs * 0.5;
}

export class RequestTimeoutError extends Error {
    constructor(timeoutMs: number) {
        super(`Request timed out after ${timeoutMs}ms`);
        // Not 'AbortError' so it is classified as a retryable transient
        // failure, distinct from a caller-initiated cancellation.
        this.name = 'TimeoutError';
    }
}

function createCancellationApiError(requestId: string): ApiError {
    // Report a caller cancellation that lands during a retry backoff the same
    // way an in-flight caller abort is reported: as a non-retried abort.
    const abortError = new Error('The operation was aborted');
    abortError.name = 'AbortError';
    return new ApiError(abortError.message, undefined, undefined, undefined, {
        originalError: abortError,
        isNetworkError: true,
        isAbortError: true,
        requestId,
    });
}

async function fetchWithTimeout<T>(
    fetch: Fetch,
    url: string,
    init: Omit<Parameters<Fetch>[1], 'signal'>,
    callerSignal: AbortSignal | undefined,
    timeoutMs: number,
    consumeResponse: (response: FetchResponse) => Promise<T>,
): Promise<T> {
    // Bound every request with a timeout so a stuck connection (e.g. a hung
    // transpacific round trip) fails fast instead of hanging on the platform
    // default. Use a manual AbortController + setTimeout rather than
    // AbortSignal.timeout/any so this works in React Native too.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    // Forward an explicit caller cancellation to our internal controller
    // (without AbortSignal.any) so caller-initiated aborts still work.
    const forwardCallerAbort = (): void => {
        controller.abort();
    };
    if (callerSignal) {
        if (callerSignal.aborted) {
            controller.abort();
        } else {
            callerSignal.addEventListener('abort', forwardCallerAbort);
        }
    }

    try {
        const response = await fetch(url, {
            ...init,
            signal: controller.signal,
        });
        return await consumeResponse(response);
    } catch (error: unknown) {
        // A timeout aborts through our controller and surfaces as an
        // AbortError; re-label it as a timeout so it is retried like other
        // transient failures. A real caller cancellation stays an AbortError
        // so it propagates as a cancellation and is never retried.
        if (
            timedOut &&
            !(callerSignal?.aborted ?? false) &&
            isAbortError(error)
        ) {
            throw new RequestTimeoutError(timeoutMs);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener('abort', forwardCallerAbort);
    }
}

async function readResponseBody(response: FetchResponse): Promise<unknown> {
    if (response.status === 204) {
        return null;
    }
    if (isJsonResponse(response)) {
        return response.json();
    }
    return response.ok ? response.blob() : response.text();
}

type RawApiResult = {
    readonly response: FetchResponse;
    readonly body: unknown;
};

export function createApiClient(config: ApiClientConfig) {
    const rateLimitRecovery = createRateLimitRecovery();

    function buildUrl<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        options: Pick<
            ApiRequestOptions<Path, Method>,
            'url' | 'pathParams' | 'queryParams'
        >,
    ): string {
        let finalUrl = (options.url as string).slice(1);
        if (options.pathParams) {
            Object.entries(options.pathParams).forEach(([key, value]) => {
                // encodeURIComponent so values with spaces / slashes /
                // unicode (e.g. canonical tag keys like
                // "ok with client smoking") produce well-formed URLs
                // and resolve to the right FastAPI path segment. UUIDs
                // and existing kebab-case slugs encode to themselves,
                // so this is a no-op for those callers.
                finalUrl = finalUrl.replace(
                    `{${key}}`,
                    encodeURIComponent(value),
                );
            });
        }

        let queryString = '';
        if (options.queryParams) {
            const search = new URLSearchParams();
            for (const [key, value] of Object.entries(
                options.queryParams as Record<string, unknown>,
            )) {
                if (value === undefined || value === null) {
                    continue;
                }
                if (Array.isArray(value)) {
                    for (const v of value) {
                        if (v === undefined || v === null || v === '') {
                            continue;
                        }
                        search.append(key, String(v));
                    }
                } else {
                    search.append(key, String(value));
                }
            }
            const serializedQuery = search.toString();
            if (serializedQuery) {
                queryString = `?${serializedQuery}`;
            }
        }

        return `${config.baseUrl}/${finalUrl}${queryString}`;
    }

    async function requestRaw<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        rawMethod: string,
        options: RawApiRequestOptions<Path, Method>,
        { resilient }: { readonly resilient: boolean },
    ): Promise<RawApiResult> {
        const method = rawMethod.toUpperCase();
        const httpMethod = isHttpMethod(method) ? method : undefined;
        const requestId = options.requestId ?? generateUuid();
        const headers: Record<string, string> = {
            ...options.headers,
            'X-Request-ID': requestId,
        };

        if (config.clientVersion) {
            headers['X-Client-Version'] = config.clientVersion;
        }

        const mode =
            config.getMode?.(options.mode) ??
            options.mode ??
            config.defaultMode;
        if (mode) {
            headers['X-Mode'] = mode;
        }

        if (options.reason) {
            headers['X-Source'] = options.reason;
        }

        let body = options.body;
        if (method !== 'GET') {
            body ??= {} as ApiBodyType<Path, Method>;
        }

        const isFormDataBody =
            typeof FormData !== 'undefined' && body instanceof FormData;
        if (body && !isFormDataBody) {
            headers['Content-Type'] = 'application/json';
        }

        const apiUrl = buildUrl<Path, Method>(options);
        // Several test suites replace global URL with an object-URL stub, so
        // shared request-path code must avoid relying on its constructor.
        const pathname = apiUrl
            .slice(config.baseUrl.length)
            .replace(/\?.*$/, '');
        const routeRateLimitCooldownKey = `${method} ${pathname}`;
        // The client timeout and transient retry only apply to idempotent
        // (GET/HEAD) requests on the JSON request() path. Non-idempotent
        // mutations opt out of the timeout because aborting them client-side
        // would not stop the server from completing, so a slow synchronous
        // POST (e.g. an inline LLM generation) must not be turned into a
        // false failure. Streaming raw() callers (e.g. the agent-run
        // long-poll) own their own reconnect loop and must keep the caller's
        // AbortSignal wired straight to fetch, so they opt out too.
        const transparentRateLimitRetryEligible =
            isTransparentRateLimitRecoveryEligible(
                httpMethod,
                options.idempotent,
            );
        const applyResilience = resilient && transparentRateLimitRetryEligible;
        const retryTransientFailures =
            applyResilience && options.retry !== false;
        const maximumRequestAttempts = transparentRateLimitRetryEligible
            ? MAX_REQUEST_ATTEMPTS
            : 1;
        const rateLimitFailureMode =
            options.rateLimitFailureMode ??
            (httpMethod !== undefined && isRetryableMethod(httpMethod)
                ? 'silent'
                : 'notify');
        const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
        const requestBody: BodyInit | undefined = isFormDataBody
            ? (body as FormData)
            : body
              ? JSON.stringify(body)
              : undefined;
        const requestInit = {
            method,
            headers,
            body: requestBody,
            ...(options.cache === undefined ? {} : { cache: options.cache }),
            ...(options.redirect === undefined
                ? {}
                : { redirect: options.redirect }),
        };

        let pendingRateLimitRetry: RateLimitRetryOwner | undefined;
        let lastError: ApiError | undefined;
        let networkAttemptCount = 0;
        const retryWaitBudget = createRetryWaitBudget();

        async function waitUntilRetry(retryAtMs: number): Promise<void> {
            const delayMs = Math.max(0, retryAtMs - Date.now());
            if (delayMs === 0) {
                return;
            }
            await sleep(delayMs, options.signal);
            if (options.signal?.aborted) {
                throw createCancellationApiError(requestId);
            }
        }

        async function finalizeRequestError(error: ApiError): Promise<never> {
            return finalizeTransparentRateLimitError({
                error,
                resilient,
                method: httpMethod,
                signal: options.signal,
                url: apiUrl,
                failureMode: rateLimitFailureMode,
                onExhausted: config.onTransparentRateLimitExhausted,
                createCancellationError: () =>
                    createCancellationApiError(requestId),
                onPresentationError: (callbackError, presentedMethod) => {
                    config.onNetworkError?.({
                        error: callbackError,
                        apiUrl,
                        method: presentedMethod,
                        pathTemplate: options.url as string,
                        requestId,
                        attempt: networkAttemptCount,
                        maxAttempts: networkAttemptCount,
                        mode,
                        reason:
                            'transparent-rate-limit-exhaustion-' +
                            'callback-failed',
                    });
                },
            });
        }

        try {
            while (true) {
                if (
                    networkAttemptCount >= maximumRequestAttempts &&
                    lastError !== undefined
                ) {
                    await finalizeRequestError(lastError);
                }

                if (pendingRateLimitRetry !== undefined) {
                    const retryAtMs = rateLimitRecovery.getPendingRetryAtMs(
                        pendingRateLimitRetry,
                    );
                    if (retryAtMs !== undefined && retryAtMs > Date.now()) {
                        if (!retryWaitBudget.fits(retryAtMs)) {
                            await finalizeRequestError(lastError!);
                        }
                        await waitUntilRetry(retryAtMs);
                        continue;
                    }
                }

                const activeCooldown = isRetryableMethod(method)
                    ? rateLimitRecovery.getActiveGlobalCooldown()
                    : null;
                if (activeCooldown !== null) {
                    lastError = new ApiError(
                        activeCooldown.message,
                        activeCooldown.status,
                        undefined,
                        undefined,
                        {
                            globalOverloadRetryAtMs: activeCooldown.retryAtMs,
                            requestId,
                            transparentRateLimitSource: 'global-overload',
                        },
                    );
                    await finalizeRequestError(lastError);
                }

                // An exact method-and-path cooldown can safely suppress the
                // same mutation. A principal-wide cooldown gates only reads,
                // or one read burst could block an unrelated mutation such as
                // acquiring a websocket ticket and break live features.
                const activePrincipalRateLimitCooldown = isRetryableMethod(
                    method,
                )
                    ? rateLimitRecovery.getActivePrincipalCooldown()
                    : null;
                if (activePrincipalRateLimitCooldown !== null) {
                    lastError = createRateLimitSuppressedApiError(
                        activePrincipalRateLimitCooldown,
                        requestId,
                    );
                    await finalizeRequestError(lastError);
                }

                const activeRouteRateLimitCooldown =
                    rateLimitRecovery.getActiveRouteCooldown(
                        routeRateLimitCooldownKey,
                    );
                if (activeRouteRateLimitCooldown !== null) {
                    lastError = createRateLimitSuppressedApiError(
                        activeRouteRateLimitCooldown,
                        requestId,
                    );
                    await finalizeRequestError(lastError);
                }

                // Raw requests also retry classified 429s, so cancellation
                // must gate every fetch independently of JSON resilience.
                if (options.signal?.aborted) {
                    throw createCancellationApiError(requestId);
                }

                let response: FetchResponse;
                let responseBody: unknown = undefined;
                let responseBodyWasRead = false;
                const retryOwnerForFetch = pendingRateLimitRetry;
                networkAttemptCount += 1;
                try {
                    try {
                        if (applyResilience) {
                            const result = await fetchWithTimeout(
                                config.fetch,
                                apiUrl,
                                requestInit,
                                options.signal,
                                timeoutMs,
                                async (candidate) => ({
                                    response: candidate,
                                    body: await readResponseBody(candidate),
                                }),
                            );
                            response = result.response;
                            responseBody = result.body;
                            responseBodyWasRead = true;
                            if (options.signal?.aborted) {
                                throw createCancellationApiError(requestId);
                            }
                        } else {
                            response = await config.fetch(apiUrl, {
                                ...requestInit,
                                signal: options.signal,
                            });
                            if (resilient && options.signal?.aborted) {
                                throw createCancellationApiError(requestId);
                            }
                            if (resilient) {
                                responseBody = await readResponseBody(response);
                                responseBodyWasRead = true;
                                if (options.signal?.aborted) {
                                    throw createCancellationApiError(requestId);
                                }
                            }
                        }
                    } finally {
                        if (retryOwnerForFetch !== undefined) {
                            rateLimitRecovery.releaseRetryOwner(
                                retryOwnerForFetch,
                            );
                            if (pendingRateLimitRetry === retryOwnerForFetch) {
                                pendingRateLimitRetry = undefined;
                            }
                        }
                    }
                } catch (error: unknown) {
                    const requestError =
                        error instanceof ApiError
                            ? error
                            : new ApiError(
                                  error instanceof Error
                                      ? error.message
                                      : 'Network request failed',
                                  undefined,
                                  undefined,
                                  undefined,
                                  {
                                      originalError: error,
                                      isNetworkError: true,
                                      isAbortError: isAbortError(error),
                                      requestId,
                                  },
                              );
                    lastError = requestError;
                    const reportedError = requestError.originalError ?? error;
                    const maxNetworkAttempts = retryTransientFailures
                        ? MAX_REQUEST_ATTEMPTS
                        : networkAttemptCount;
                    const networkErrorContext = {
                        error: reportedError,
                        apiUrl,
                        method,
                        pathTemplate: options.url as string,
                        requestId,
                        attempt: networkAttemptCount,
                        maxAttempts: maxNetworkAttempts,
                        mode,
                        reason: options.reason,
                        bestEffort: options.bestEffort,
                    } satisfies ApiClientNetworkErrorContext;
                    const willRetry =
                        retryTransientFailures &&
                        shouldRetryApiClientNetworkError(networkErrorContext);
                    config.onNetworkError?.(networkErrorContext);

                    if (!willRetry) {
                        throw requestError;
                    }

                    const retryAtMs =
                        Date.now() + getRetryDelayMs(networkAttemptCount);
                    if (!retryWaitBudget.admit(retryAtMs)) {
                        throw requestError;
                    }
                    await waitUntilRetry(retryAtMs);
                    continue;
                }

                if (response.ok) {
                    return { response, body: responseBody };
                }

                const retryAfterDelayMs = getRetryAfterDelayMs(response);
                let errorMessage = `Request failed with status: ${response.status}`;
                if (!responseBodyWasRead) {
                    responseBody = await readResponseBody(response);
                }
                const errorBody = responseBody;

                errorMessage = getApiErrorMessage(errorBody) ?? errorMessage;
                const retryAtMs =
                    retryAfterDelayMs === null
                        ? undefined
                        : Date.now() + retryAfterDelayMs;
                const {
                    source: transparentRateLimitSource,
                    isGlobalOverload,
                    rateLimitedScope,
                    rateLimitedRetryAtMs,
                    cooldownArmed: transparentCooldownArmed,
                } = rateLimitRecovery.recordResponseCooldowns({
                    response,
                    retryAtMs,
                    routeKey: routeRateLimitCooldownKey,
                    message: errorMessage,
                });

                lastError = new ApiError(
                    errorMessage,
                    response.status,
                    response,
                    errorBody,
                    {
                        requestId:
                            getApiErrorRequestId(errorBody) ??
                            response.headers.get('x-request-id') ??
                            requestId,
                        globalOverloadRetryAtMs: isGlobalOverload
                            ? retryAtMs
                            : undefined,
                        rateLimitedScope,
                        rateLimitedRetryAtMs,
                        rateLimitSuppressed:
                            response.status === 429 ? false : undefined,
                        transparentRateLimitSource:
                            transparentRateLimitSource ?? undefined,
                    },
                );

                // Classification and a valid deadline govern transparent
                // recovery because global overload uses both 429 and 503.
                if (
                    transparentRateLimitSource !== null &&
                    retryAtMs !== undefined &&
                    transparentCooldownArmed &&
                    transparentRateLimitRetryEligible &&
                    networkAttemptCount < maximumRequestAttempts
                ) {
                    if (
                        !rateLimitRecovery.hasRetryOwner(
                            transparentRateLimitSource,
                            routeRateLimitCooldownKey,
                        )
                    ) {
                        // A fetch can outlast the budget, so even a zero wait
                        // starts the clock before another attempt is admitted.
                        if (!retryWaitBudget.admit(retryAtMs)) {
                            await finalizeRequestError(lastError);
                        }
                        pendingRateLimitRetry =
                            rateLimitRecovery.claimRetryOwner(
                                transparentRateLimitSource,
                                routeRateLimitCooldownKey,
                            );
                        if (pendingRateLimitRetry !== undefined) {
                            continue;
                        }
                    }
                }

                // Retry-After remains authoritative for ordinary server
                // failures; transparent 429 recovery uses its stored cooldown.
                if (
                    retryTransientFailures &&
                    networkAttemptCount < maximumRequestAttempts &&
                    retryAfterDelayMs === null &&
                    isRetryableHttpStatus(response.status)
                ) {
                    const retryAtMs =
                        Date.now() + getRetryDelayMs(networkAttemptCount);
                    if (!retryWaitBudget.admit(retryAtMs)) {
                        await finalizeRequestError(lastError);
                    }
                    await waitUntilRetry(retryAtMs);
                    continue;
                }

                await finalizeRequestError(lastError);
            }
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(
                error instanceof Error
                    ? error.message
                    : 'Network request failed',
                undefined,
                undefined,
                undefined,
                { requestId },
            );
        } finally {
            if (pendingRateLimitRetry !== undefined) {
                rateLimitRecovery.releaseRetryOwner(pendingRateLimitRetry);
            }
        }
    }

    async function request<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        method: string,
        options: ApiRequestOptions<Path, Method>,
    ): Promise<ApiResponseType<Path, Method>> {
        const { response, body } = await requestRaw<Path, Method>(
            method,
            options,
            {
                resilient: true,
            },
        );

        if (response.status === 204) {
            return null as ApiResponseType<Path, Method>;
        }

        return body as ApiResponseType<Path, Method>;
    }

    return {
        raw: <
            Path extends keyof paths,
            Method extends keyof paths[Path] = DefaultRawApiMethod<Path>,
        >(
            params: RawApiRequestOptions<Path, Method> & {
                readonly method: string;
            },
        ) =>
            requestRaw<Path, Method>(params.method, params, {
                resilient: false,
            }).then((result) => result.response),

        get: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'get',
        >(params: {
            readonly url: Path;
            readonly pathParams?: ApiPathParamsType<Path>;
            readonly queryParams?: ApiQueryParamsType<Path, Method>;
            readonly mode?: Mode;
            readonly rateLimitFailureMode?: 'silent' | 'notify';
            readonly reason?: string;
            readonly requestId?: string;
            readonly signal?: AbortSignal;
            readonly timeoutMs?: number;
            readonly retry?: boolean;
        }) => request<Path, Method>('GET', params),

        post: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'post',
        >(params: {
            readonly url: Path;
            readonly body?: ApiBodyType<Path, Method>;
            readonly pathParams?: ApiPathParamsType<Path>;
            readonly queryParams?: ApiQueryParamsType<Path, Method>;
            readonly mode?: Mode;
            readonly rateLimitFailureMode?: 'silent' | 'notify';
            readonly reason?: string;
            readonly requestId?: string;
            readonly signal?: AbortSignal;
            readonly idempotent?: boolean;
            readonly bestEffort?: boolean;
        }) => request<Path, Method>('POST', params),

        patch: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'patch',
        >(params: {
            readonly url: Path;
            readonly body?: ApiBodyType<Path, Method>;
            readonly pathParams?: ApiPathParamsType<Path>;
            readonly queryParams?: ApiQueryParamsType<Path, Method>;
            readonly mode?: Mode;
            readonly rateLimitFailureMode?: 'silent' | 'notify';
            readonly reason?: string;
            readonly requestId?: string;
            readonly signal?: AbortSignal;
            readonly idempotent?: boolean;
        }) => request<Path, Method>('PATCH', params),

        put: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'put',
        >(params: {
            readonly url: Path;
            readonly body?: ApiBodyType<Path, Method>;
            readonly pathParams?: ApiPathParamsType<Path>;
            readonly queryParams?: ApiQueryParamsType<Path, Method>;
            readonly mode?: Mode;
            readonly rateLimitFailureMode?: 'silent' | 'notify';
            readonly reason?: string;
            readonly requestId?: string;
            readonly signal?: AbortSignal;
            readonly idempotent?: boolean;
        }) => request<Path, Method>('PUT', params),

        delete: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'delete',
        >(params: {
            readonly url: Path;
            readonly body?: ApiBodyType<Path, Method>;
            readonly pathParams?: ApiPathParamsType<Path>;
            readonly queryParams?: ApiQueryParamsType<Path, Method>;
            readonly mode?: Mode;
            readonly rateLimitFailureMode?: 'silent' | 'notify';
            readonly reason?: string;
            readonly requestId?: string;
            readonly signal?: AbortSignal;
            readonly idempotent?: boolean;
        }) => request<Path, Method>('DELETE', params),
    };
}
